import { useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { CALC } from '@/constants/testIds';

export default function CalculatorCard({ calc, open: controlledOpen, onToggle }) {
  const initial = useMemo(() => {
    const o = {};
    calc.inputs.forEach((i) => { o[i.key] = i.default ?? ''; });
    return o;
  }, [calc]);
  const [values, setValues] = useState(initial);
  const [internalOpen, setInternalOpen] = useState(true);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const toggle = () => (onToggle ? onToggle() : setInternalOpen((o) => !o));

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const results = calc.compute(values);
  const hasAnyInput = calc.inputs.some((i) => {
    if (i.type === 'select') return false;
    const v = values[i.key];
    if (i.type === 'text' || i.type === 'textarea') return typeof v === 'string' && v.trim() !== '';
    return v !== '' && v !== null && v !== undefined && parseFloat(v) !== 0;
  });

  return (
    <article data-testid={CALC.card(calc.slug)} className="tm-card p-5 sm:p-6 flex flex-col gap-4 bg-white">
      <button
        type="button"
        data-testid={CALC.toggle(calc.slug)}
        onClick={toggle}
        aria-expanded={open}
        className="text-left w-full group"
      >
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display font-bold text-lg sm:text-xl tracking-tight uppercase leading-tight">{calc.name}</h3>
          <div className="flex items-center gap-2 shrink-0">
            <span className="tm-chip tm-chip-orange">{calc.slug.split('-')[0]}</span>
            <ChevronDown
              size={20}
              className={`text-slate-500 transition-transform duration-200 group-hover:text-brand ${open ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
        {open && <p className="text-sm text-slate-600 mt-1">{calc.blurb}</p>}
      </button>

      {open && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {calc.inputs.map((inp) => (
              <label key={inp.key} className={`block ${inp.full ? 'sm:col-span-2' : ''}`}>
                <span className="tm-label">{inp.label}{inp.unit ? ` (${inp.unit})` : ''}</span>
                {inp.type === 'select' ? (
                  <select
                    data-testid={CALC.input(calc.slug, inp.key)}
                    value={values[inp.key]}
                    onChange={(e) => set(inp.key, e.target.value)}
                    className="tm-input bg-white"
                  >
                    {inp.options.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : inp.type === 'textarea' || inp.type === 'text' ? (
                  inp.type === 'textarea' ? (
                    <textarea
                      data-testid={CALC.input(calc.slug, inp.key)}
                      rows={2}
                      placeholder={inp.placeholder}
                      value={values[inp.key]}
                      onChange={(e) => set(inp.key, e.target.value)}
                      className="tm-input font-mono text-sm"
                    />
                  ) : (
                    <input
                      data-testid={CALC.input(calc.slug, inp.key)}
                      type="text"
                      placeholder={inp.placeholder}
                      value={values[inp.key]}
                      onChange={(e) => set(inp.key, e.target.value)}
                      className="tm-input font-mono text-sm"
                    />
                  )
                ) : (
                  <input
                    data-testid={CALC.input(calc.slug, inp.key)}
                    type="number"
                    inputMode="decimal"
                    step="any"
                    placeholder={inp.placeholder}
                    value={values[inp.key]}
                    onChange={(e) => set(inp.key, e.target.value)}
                    className="tm-input"
                  />
                )}
              </label>
            ))}
          </div>

          <div data-testid={CALC.result(calc.slug)} className="tm-result mt-1">
            {hasAnyInput ? (
              <ul className="space-y-1.5">
                {results.map((r, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-3 border-b border-slate-700/40 pb-1 last:border-0 last:pb-0">
                    <span className="text-xs uppercase tracking-widest text-slate-300">{r.label}</span>
                    <span className="font-mono text-lg sm:text-xl font-bold text-brand">
                      {typeof r.value === 'number' ? r.value.toLocaleString() : r.value}
                      {r.unit ? <span className="text-slate-300 text-sm ml-1">{r.unit}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="font-mono text-sm text-slate-400">Enter values to calculate →</p>
            )}
          </div>

          <div className="flex items-end justify-between gap-2">
            <button
              data-testid={CALC.resetBtn(calc.slug)}
              onClick={() => setValues(initial)}
              className="tm-btn tm-btn-ghost text-xs py-2 min-h-0"
            >
              Reset
            </button>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 text-right leading-tight max-w-[60%] font-semibold">
              Estimate only — verify before quoting
            </p>
          </div>
        </>
      )}
    </article>
  );
}
